/**
 * useConversationAttachments - Hook para gerenciar anexos de uma conversa
 * 
 * Implementa:
 * - Cache compartilhado entre sidebar e galeria via React Query
 * - Query leve de contagem via RPC (get_conversation_attachments_count)
 * - Query de preview via RPC (get_conversation_attachments_preview)
 * - Paginação server-side via RPC (get_conversation_attachments_page)
 * 
 * Versão: 2.0.0 - Migrado de queries REST diretas para RPCs otimizadas
 */

import { useQuery, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

// Logger com namespace para este hook
const logger = createLogger('ConversationAttachments');

// Constantes de paginação
const PAGE_SIZE = 20;

// Interface do anexo exportada para uso nos componentes
export interface ConversationAttachment {
  id: string;
  type: string;
  url: string;
  filename: string | null;
  mime_type: string | null;
  thumbnail_url: string | null;
  size_bytes: number | null;
  created_at: string;
}

interface UseConversationAttachmentsOptions {
  conversationId: string | null;
  enabled?: boolean;
}

export function useConversationAttachments({
  conversationId,
  enabled = true,
}: UseConversationAttachmentsOptions) {
  const queryClient = useQueryClient();

  // =========================================================================
  // QUERY 1: Contagem total via RPC (leve, apenas COUNT no banco)
  // =========================================================================
  const countQuery = useQuery({
    queryKey: ['conversation-attachments-count', conversationId],
    queryFn: async () => {
      if (!conversationId) return 0;

      logger.debug(`Buscando contagem de anexos via RPC para ${conversationId}`);

      const { data, error } = await supabase.rpc('get_conversation_attachments_count', {
        p_conversation_id: conversationId,
      });

      if (error) {
        logger.error(`Erro ao contar anexos: ${error.message}`);
        throw error;
      }

      const count = typeof data === 'number' ? data : 0;
      logger.debug(`Contagem de anexos: ${count}`);
      return count;
    },
    enabled: !!conversationId && enabled,
    staleTime: 60000, // 1 minuto - evita refetch desnecessário
  });

  // =========================================================================
  // QUERY 2: Preview via RPC (3 últimos para sidebar)
  // =========================================================================
  const previewQuery = useQuery({
    queryKey: ['conversation-attachments-preview', conversationId],
    queryFn: async (): Promise<ConversationAttachment[]> => {
      if (!conversationId) return [];

      logger.debug(`Buscando preview de anexos via RPC para ${conversationId}`);

      const { data, error } = await supabase.rpc('get_conversation_attachments_preview', {
        p_conversation_id: conversationId,
      });

      if (error) {
        logger.error(`Erro ao buscar preview: ${error.message}`);
        throw error;
      }

      logger.debug(`Preview carregado: ${data?.length || 0} itens`);
      return (data || []) as ConversationAttachment[];
    },
    enabled: !!conversationId && enabled,
    staleTime: 60000, // 1 minuto
  });

  // =========================================================================
  // QUERY 3: Galeria com paginação infinita via RPC (sob demanda)
  // =========================================================================
  const galleryQuery = useInfiniteQuery({
    queryKey: ['conversation-attachments-gallery', conversationId],
    queryFn: async ({ pageParam }): Promise<ConversationAttachment[]> => {
      if (!conversationId) return [];

      logger.debug(`Buscando página da galeria via RPC para ${conversationId} (cursor: ${pageParam || 'inicial'})`);

      const { data, error } = await supabase.rpc('get_conversation_attachments_page', {
        p_conversation_id: conversationId,
        p_cursor: pageParam || undefined,
        p_page_size: PAGE_SIZE,
      });

      if (error) {
        logger.error(`Erro ao buscar página da galeria: ${error.message}`);
        throw error;
      }

      logger.debug(`Página da galeria carregada: ${data?.length || 0} itens`);
      return (data || []) as ConversationAttachment[];
    },
    getNextPageParam: (lastPage) => {
      // Se retornou menos que PAGE_SIZE, não há mais páginas
      if (lastPage.length < PAGE_SIZE) return undefined;
      // Cursor é o created_at do último item
      return lastPage[lastPage.length - 1]?.created_at;
    },
    initialPageParam: null as string | null,
    enabled: false, // NÃO carrega automaticamente - só quando galeria abre
  });

  // =========================================================================
  // Funções auxiliares
  // =========================================================================
  
  /**
   * Inicia carregamento da galeria (chamado quando modal abre)
   * Só dispara refetch se não houver dados em cache
   */
  const loadGallery = () => {
    if (!galleryQuery.data && !galleryQuery.isFetching) {
      galleryQuery.refetch();
    }
  };

  /**
   * Invalida todo o cache de anexos da conversa
   * Útil após envio de novo anexo ou exclusão
   */
  const invalidateCache = () => {
    queryClient.invalidateQueries({ 
      queryKey: ['conversation-attachments-count', conversationId] 
    });
    queryClient.invalidateQueries({ 
      queryKey: ['conversation-attachments-preview', conversationId] 
    });
    queryClient.invalidateQueries({ 
      queryKey: ['conversation-attachments-gallery', conversationId] 
    });
  };

  return {
    // Dados para sidebar (carrega automaticamente)
    previewAttachments: previewQuery.data || [],
    totalCount: countQuery.data || 0,
    isLoadingPreview: previewQuery.isLoading || countQuery.isLoading,

    // Dados para galeria (carrega sob demanda)
    galleryAttachments: galleryQuery.data?.pages.flat() || [],
    isLoadingGallery: galleryQuery.isLoading,
    isFetchingMore: galleryQuery.isFetchingNextPage,
    hasMorePages: !!galleryQuery.hasNextPage,
    fetchNextPage: galleryQuery.fetchNextPage,
    loadGallery,

    // Utilitários
    invalidateCache,
  };
}
